import { StateCreator } from "zustand";
import { toast } from "react-toastify";
import { CommonResponseModel } from "../../../../Models/tableDetailsModel";
import agentsApi from "../../agents.api";
import { AgentDetailSaveScope, IAgentDetails } from "../../agentDetail.model";
import { AgentsStore } from "../agentsTypes";
import { initialAgentDetailState } from "./agentDetailState";
import { AgentDetailAsyncActions } from "./agentDetailTypes";
import {
    buildAgentSavePayload,
    cloneAgentDetails,
    isScopeDirty,
} from "./agentDetail.utils";
import { selectHasErrorsByScope } from "./agentDetailSelectors";
import { getApiErrorMessage } from "../../../../utils/getApiErrorMessage";
import { shouldShowErrorToast } from "../../../../utils/apiRequestError";

export const createAgentDetailAsyncActions: StateCreator<AgentsStore, [], [], AgentDetailAsyncActions> = (
    set,
    get
) => ({
    fetchAgentDetails: async (userId, opts) => {
        const { setShowLoader, silent = false } = opts ?? {};
        const { detail } = get();

        if (!silent && detail.status === "loading") return false;

        set({
            detail: {
                ...(detail.activeUserId === userId ? detail : initialAgentDetailState),
                activeUserId: userId,
                ...(silent
                    ? {}
                    : {
                          status: "loading",
                          error: null,
                      }),
            },
        });
        if (!silent) setShowLoader?.(true);

        try {
            const response: CommonResponseModel<IAgentDetails> = 
                await agentsApi.GetAgentDetails.get(userId);

                // Discard stale responses
                if (get().detail.activeUserId !== userId) return false;

            if (response.flagStatus && response.value) {
                set({
                    detail: {
                        ...get().detail,
                        activeUserId: userId,
                        data: response.value,
                        draft: cloneAgentDetails(response.value),
                        status: "success",
                        error: null,
                        isEditMode: true,
                    },
                });
                return true;
            }

            set({
                detail: {
                    ...get().detail,
                    status: "error",
                    error: response.error ?? "Failed to load agent details",
                },
            });
            if (!silent) toast.error(response.error ?? "Failed to load agent details");
            return false;
        } catch (error) {
            // Discard stale responses
            if (get().detail.activeUserId !== userId) return false;

            const message = getApiErrorMessage(error, "Failed to load agent details");
            set({
                detail: {
                    ...get().detail,
                    status: "error",
                    error: message,
                },
            });
            if (!silent && shouldShowErrorToast(error)) {
                toast.error(message);
            }
            return false;
        } finally {
            if (!silent && get().detail.activeUserId === userId) setShowLoader?.(false);
        }
    },

    saveAgentDetail: async (scope: AgentDetailSaveScope = "all") => {
        const { detail } = get();
        const { draft, data, activeUserId, saveStatus } = detail;
        if (!draft || !data || !activeUserId) return false;
        if (!isScopeDirty(draft, data, scope)) return true;
        if (saveStatus === "saving") return false;
        if (selectHasErrorsByScope(scope)(get())) return false;

        set({
            detail: {
                ...detail,
                saveStatus: "saving",
                saveError: null,
            },
        });

        try {
            const payload = { userId: activeUserId, ...buildAgentSavePayload(draft, data, scope) };
            const response: CommonResponseModel<string> = await agentsApi.UpdateAgentDetails.post(payload);

            if (!response?.flagStatus) {
                const errorMessage = response?.error ?? "Failed to save agent details";
                set({
                    detail: {
                        ...get().detail,
                        saveStatus: "error",
                        saveError: errorMessage,
                    },
                });
                toast.error(errorMessage);
                return false;
            }

            const refetchSuccess = await get().fetchAgentDetails(activeUserId, { silent: true });
            set({
                hasLoadedAgents: false,
                detail: {
                    ...get().detail,
                    saveStatus: refetchSuccess ? "idle" : "error",
                    saveError: refetchSuccess ? null : "Agent saved but failed to refresh details",
                },
            });
            if (refetchSuccess) {
                toast.success("Agent details saved successfully");
            } else {
                toast.error("Agent saved but failed to refresh details");
            }
            return refetchSuccess;
        } catch (error) {
            const errorMessage = getApiErrorMessage(error, "Failed to save agent details");
            set({
                detail: {
                    ...get().detail,
                    saveStatus: "error",
                    saveError: errorMessage,
                },
            });
            if (shouldShowErrorToast(error)) {
                toast.error(errorMessage);
            }
            return false;
        }
    },
});
